import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { Loader2, ScrollText, ChevronLeft, ChevronRight } from 'lucide-react';
import adminAxios from './adminAxios';

const ACTION_LABELS = {
  toggle_company: 'Empresa ativada/inativada',
  toggle_gym: 'Academia ativada/inativada',
  toggle_student: 'Aluno ativado/inativado',
  approve_student: 'Aluno aprovado',
  set_discount: 'Desconto alterado',
  delete_company: 'Empresa excluída',
  delete_gym: 'Academia excluída',
  delete_student: 'Aluno excluído',
  disable_2fa: '2FA desativado',
  approve_coparticipation: 'Coparticipação ativada',
  reject_student_request: 'Solicitação rejeitada',
  send_notification: 'Notificação enviada',
};

const AdminAuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter !== 'all') params.append('action_type', filter);
      const res = await adminAxios.get(`${API}/admin/audit-log?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch {
      toast.error('Erro ao carregar log');
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const getActionColor = (action) => {
    if (action.includes('delete')) return 'bg-red-100 text-red-700';
    if (action.includes('approve')) return 'bg-emerald-100 text-emerald-700';
    if (action.includes('toggle')) return 'bg-amber-100 text-amber-700';
    if (action.includes('discount')) return 'bg-blue-100 text-blue-700';
    return 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6" data-testid="admin-audit-log-section">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
          <ScrollText className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Log de Atividades</h2>
          <p className="text-sm text-slate-500">Histórico de ações administrativas ({total} registros)</p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {['all', 'toggle', 'approve', 'delete', 'discount', '2fa'].map(f => (
          <button
            key={f}
            onClick={() => { setFilter(f); setPage(1); }}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full transition ${
              filter === f ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid={`audit-filter-${f}`}
          >
            {f === 'all' ? 'Todos' : f === 'toggle' ? 'Status' : f === 'approve' ? 'Aprovações' :
             f === 'delete' ? 'Exclusões' : f === 'discount' ? 'Descontos' : '2FA'}
          </button>
        ))}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : logs.length === 0 ? (
            <div className="p-12 text-center">
              <ScrollText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nenhuma atividade registrada</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {logs.map(log => (
                <div key={log.log_id} className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50">
                  <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${getActionColor(log.action)}`}>
                    {ACTION_LABELS[log.action] || log.action}
                  </span>
                  <div className="flex-1 min-w-0">
                    {log.target_type && (
                      <span className="text-xs text-slate-500">
                        {log.target_type === 'company' ? 'Empresa' : log.target_type === 'gym' ? 'Academia' : log.target_type === 'student' ? 'Aluno' : log.target_type}
                        {log.details?.name ? ` — ${log.details.name}` : ''}
                      </span>
                    )}
                    {log.details?.amount !== undefined && (
                      <span className="text-xs text-blue-600 ml-2">R$ {log.details.amount.toFixed(2).replace('.', ',')}</span>
                    )}
                    {log.details?.is_active !== undefined && (
                      <span className={`text-xs ml-2 ${log.details.is_active ? 'text-emerald-600' : 'text-red-500'}`}>
                        {log.details.is_active ? 'Ativado' : 'Inativado'}
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] text-slate-400 whitespace-nowrap">
                    {new Date(log.timestamp).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-slate-600">{page} / {pages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminAuditLog;
